import { useEffect, useState } from 'react'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'

import { DialogPelaksanaanAppealApprove } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealApprove'
import { DialogPelaksanaanAppealApproved } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealApproved'
import { DialogPelaksanaanAppealReject } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealReject'
import { DialogPelaksanaanAppealRejected } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealRejected'
import { useAPIPostPraktikKinerjaRollbackStateApproval } from 'api/kinerja/pengawas-sekolah/pelaksanaan/form'
import CardNotificationBase from './CardNotificationBase'

import type { CardNotificationBaseProps } from 'app/KinerjaNotifikasiPage/components/CardNotification/CardNotificationBase'
import type {
  StatusPelaksanaanPraktikKinerja,
  TPraktikKinerjaFormName,
} from 'types/kinerja-ps-pegawai'

type CardNotificationPelaksanaanAppealPSProps = CardNotificationBaseProps & {
  appealState?: string
  editState?: StatusPelaksanaanPraktikKinerja
  currentState?: StatusPelaksanaanPraktikKinerja
  notificationId?: number
  skpId?: string
  name?: string
}

const MSG_ERROR_SUBMIT = 'Gagal ubah menjadi draf. Coba lagi.'

const CardNotificationPelaksanaanAppealPS = ({
  appealState,
  editState,
  currentState,
  skpId,
  name,
  ...cardProps
}: CardNotificationPelaksanaanAppealPSProps) => {
  const toast = useToast()
  const [approvalState, setApprovalState] = useState<
    'APPROVED' | 'REJECTED' | undefined
  >(undefined)

  const [activeDialog, setActiveDialog] = useState<
    | 'NONE'
    | 'APPEAL_APPROVE'
    | 'APPEAL_REJECT'
    | 'APPEAL_APPROVED'
    | 'APPEAL_REJECTED'
  >('NONE')
  const handleCloseDialog = () => setActiveDialog('NONE')

  const handleApproveCTA = () => {
    setActiveDialog('APPEAL_APPROVE')
  }

  const handleRejectCTA = () => {
    setActiveDialog('APPEAL_REJECT')
  }

  const { mutateAsync, isLoading: isSubmitting } =
    useAPIPostPraktikKinerjaRollbackStateApproval({
      skpPsId: skpId,
      formName: (editState ?? '').toLowerCase() as TPraktikKinerjaFormName,
      approvalState,
    })

  const handleRollbackApproval = async (state: 'APPROVED' | 'REJECTED') => {
    try {
      await mutateAsync({})
      setActiveDialog(
        state === 'APPROVED' ? 'APPEAL_APPROVED' : 'APPEAL_REJECTED'
      )
    } catch (err) {
      toast({ message: MSG_ERROR_SUBMIT })
      setActiveDialog('NONE')
    } finally {
      setApprovalState(undefined)
    }
  }

  const renderAppealResult = (
    state: CardNotificationPelaksanaanAppealPSProps['appealState']
  ) => {
    switch (state) {
      case 'APPROVED':
        return <Badge variant="success">Pengajuan sudah diterima</Badge>
      case 'REJECTED':
        return <Badge>Pengajuan sudah ditolak</Badge>
      default:
        return (
          <div className="flex mt-6 space-x-3">
            <Button size="sm" color="blue" onClick={handleApproveCTA}>
              Terima dan ulangi hasil pemantauan
            </Button>
            <Button
              size="sm"
              color="blue"
              variant="ghost"
              onClick={handleRejectCTA}
            >
              Tolak pengajuan
            </Button>
          </div>
        )
    }
  }

  useEffect(() => {
    if (Boolean(approvalState)) {
      handleRollbackApproval(approvalState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalState])

  return (
    <CardNotificationBase {...cardProps}>
      {renderAppealResult(appealState)}

      <DialogPelaksanaanAppealApprove
        isLoading={isSubmitting}
        isOpen={activeDialog === 'APPEAL_APPROVE'}
        currentState={currentState}
        editState={editState}
        onClose={handleCloseDialog}
        onConfirm={() => {
          setApprovalState('APPROVED')
        }}
      />

      <DialogPelaksanaanAppealReject
        isLoading={isSubmitting}
        isOpen={activeDialog === 'APPEAL_REJECT'}
        editState={editState}
        name={name}
        onClose={handleCloseDialog}
        onConfirm={() => {
          setApprovalState('REJECTED')
        }}
      />

      <DialogPelaksanaanAppealApproved
        isOpen={activeDialog === 'APPEAL_APPROVED'}
        onClose={handleCloseDialog}
      />

      <DialogPelaksanaanAppealRejected
        isOpen={activeDialog === 'APPEAL_REJECTED'}
        onClose={handleCloseDialog}
      />
    </CardNotificationBase>
  )
}

export default CardNotificationPelaksanaanAppealPS
